import { AfterViewInit, Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Navigation, Router } from '@angular/router';
import { Observable, combineLatest, EMPTY, forkJoin, iif, of, throwError } from 'rxjs';
import { catchError, filter, flatMap, map, tap } from 'rxjs/operators';
import { LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { SupportService } from 'src/app/core/services/support.service';
import { ICaseDetail } from 'src/app/model/support/ICaseDetail';
import { ICaseDetailViewModel } from '../../view-models/case-details-view-model';
import { ADPResourceService } from 'digital-core';

type CaseDetailCallback = (phoneNumber?: string) => Observable<ICaseDetail[]>;

@Component({
  selector: 'pgh-tech-support-history-list',
  templateUrl: './tech-support-history-list.component.html',
  styleUrls: ['./tech-support-history-list.component.scss']
})
export class TechSupportHistoryListComponent implements OnInit, AfterViewInit {

  displayedColumns: string[] = ['CreatedDate', 'Origin', 'Transcript', 'ProductCategory', 'Status'];
  caseDetails$: Observable<ICaseDetail[]> = null;
  navigation: Navigation;
  isLoading: boolean = true;
  cases: ICaseDetail[];
  pagedTranscriptList: any[] = null;
  @ViewChildren('desktopPaginator')
  paginator: QueryList<MatPaginator>;
  casesDataSource = new MatTableDataSource<ICaseDetail>();
  @ViewChildren(MatSort)
  sort: QueryList<MatSort>;
  @ViewChild('container', { read: ElementRef }) container: ElementRef;
  pageSize: number = 0;
  totalCount: number = 0;
  pgList: ICaseDetail[];

  /**
   * Gets an Observable that emits an array of case details
   * @param casesFn - Callback Function that passes a phone number and returns an case details observable
   * @returns Observable of ICaseDetailViewModel[]
   */
  fetchDetails$(casesFn: CaseDetailCallback): Observable<ICaseDetailViewModel[]> {

    // Find current user to get phone number for case details
    return combineLatest([
      this.loggedInUserSettings.$selectedContract,
      this.loggedInUserSettings.$userClaims
    ]).pipe(
      filter(([contract]) => contract !== null),
      flatMap(([contract, claims]) => {
        // current user
        const member = contract.members.find(m => m.emailAddress.toLowerCase() === claims.email.toLowerCase());

        if (!member) {
          return EMPTY;
        }

        // join cases and transcripts to create view model
        return forkJoin([
          casesFn(member.phoneNumber),
          this.supportService.getChatTranscripts(member.phoneNumber)
        ])
          .pipe(
            map(([cases, transcripts]: [ICaseDetail[], Array<any>]) => {
              this.cases = cases;
              return cases.map(val => {
                const transcript = transcripts.find(t => t.CaseId === val.Id) || null;
                const newCase: ICaseDetailViewModel = {
                  ...val,
                  HasTranscript: !!(transcript && transcript?.Body),
                  TranscriptId: transcript?.Id || null,
                  Transcript: transcript
                };

                return newCase;
              });
            }),
            catchError((err) => {
              this.isLoading = false;
              return throwError(err);
            }),
            tap({
              complete: () => this.isLoading = false
            })
          );
      })
    );
  }

  constructor(
    private router: Router,
    private loggedInUserSettings: LoggedInUserSettings,
    private supportService: SupportService,
    public resourceService: ADPResourceService,
  ) {
    this.navigation = this.router.getCurrentNavigation();
  }

  ngAfterViewInit(): void {
    this.caseDetails$.subscribe((caseList: ICaseDetail[]) => {
      if (caseList.length === 1 && caseList?.[0]?.errorCode?.length && caseList?.[0]?.errorCode === 'INVALID_SESSION_ID') {
        this.totalCount = 0;
      } else {
        this.cases = caseList;
        this.pgList = caseList.slice(0, this.pageSize > caseList.length ? caseList.length : this.pageSize);
        this.totalCount = caseList.length;
        this.casesDataSource.data = caseList;
      }
    });

    this.paginator.changes.subscribe((paginators) => {
      this.casesDataSource.paginator = paginators.first;
    });

    this.sort.changes.subscribe(sortList => {
      this.casesDataSource.sort = sortList.first;
    });
  }

  ngOnInit(): void {
    this.pageSize = parseInt(this.resourceService.getPortalSetting("Payment_History_Page_Size").Value);
    this.resourceService.transformContentImages
    // Use route data if available or fetch case details from the server again
    this.caseDetails$ = iif(
      () => !!this.navigation?.extras?.state?.caseDetails,
      // Cases from route data
      this.fetchDetails$(() => of(this.navigation.extras.state.caseDetails as ICaseDetail[])),
      // Load cases from server
      this.fetchDetails$((phoneNumber) => this.supportService.getCaseHistory(phoneNumber))
    );
  }

  formatDate(dateProp: string) {
    const date = new Date(dateProp);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return month.toString().padStart(2, '0') + '/' + day.toString().padStart(2, '0') + '/' + year;
  }

  onPageChange(event: PageEvent) {
    const startIndex = event.pageIndex * event.pageSize;
    let endIndex = startIndex + event.pageSize;

    if (endIndex > this.totalCount) {
      endIndex = this.totalCount;
    }

    this.pgList = this.cases.slice(startIndex, endIndex);

    // this.container.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
